import * as yup from 'yup';
import { invalidHppSubdomains } from '../../utils';
import { SettlementModel } from '../../types';
yup.addMethod(yup.string, 'validLengthIfPresent', function (length, message) {
    return this.test({
        name: 'test-valid-length-if-present',
        message,
        test(value) {
            return !(value && value.length > 0 && value.length !== length);
        },
    });
});
export const makeMerchantAccountPrimarySchema = yup.object({
    agencyId: yup.number().required(),
    merchantId: yup.string().max(36).required(),
});
export const availableMerchantAccountStatusesMap = {
    ACTIVE: ['ACTIVE', 'INACTIVE', 'CLOSED'],
    INACTIVE: ['ACTIVE', 'CLOSED'],
    KYC_VERIFICATION_PENDING: ['ACTIVE', 'INACTIVE'],
};
export const updateMerchantAccountStatusSchema = yup.object({
    agencyId: yup.number().required(),
    merchantId: yup.string().max(36).required(),
    status: yup.string().required().oneOf(['ACTIVE', 'INACTIVE', 'CLOSED', 'DELETED']),
});
export const hppConfigSchema = yup
    .object({
    showLogo: yup.boolean().required(),
    showTitle: yup.boolean().required(),
    showCopyright: yup.boolean().required(),
    locale: yup.string().required(),
})
    .optional();
export const hppLinkExpirationDayChoices = [30, 60, 90, 120];
export const merchantEpicStructure = yup.object({
    organizationId: yup.string().required(),
    agencyId: yup.string().required(),
    agencyName: yup.string().required(),
    branchId: yup.string().required(),
    branchName: yup.string().required(),
});
// this endpoint is a PATCH style endpoint that supports partial updates- all fields should be optional
export const makeUpdateMerchantAccountSettingsSchema = ({ maxCreditFeePercent, maxAchFeeAmount, maxEftFeeAmount, }) => yup.object({
    accountHolderCode: yup.string().required(),
    accountNickname: yup.string().optional(),
    clientId: yup.string().optional(),
    brandName: yup.string().optional(),
    brandLocation: yup
        .object({
        streetLine: yup.string(),
        address2: yup.string(),
        country: yup.string(),
        zipCode: yup.string(),
        postalCode: yup.string(),
        city: yup.string(),
        state: yup.string(),
        province: yup.string(),
        isVerified: yup.boolean(),
    })
        .nullable()
        .optional(),
    brandPhoneNumber: yup.string().optional().nullable(),
    brandImageId: yup.string().nullable().optional(),
    brandPrimaryColor: yup.string().length(7).validHex('Must be a valid hexadecimal color value').optional(),
    brandPrimaryTextColor: yup
        .string()
        .length(7)
        .validHex('Must be a valid hexadecimal color value')
        .test({
        test: (_, context) => context.parent.brandPrimaryColor !== context.parent.brandPrimaryTextColor ||
            !context.parent.brandPrimaryColor ||
            !context.parent.brandPrimaryTextColor,
        message: 'Primary Color and Text Color cannot be the same',
    })
        .optional(),
    highlightPrimaryColor: yup
        .string()
        .nullable()
        .validLengthIfPresent(7, 'Color must be six digits')
        .validHex('Must be a valid hexadecimal color value')
        .optional(),
    highlightPrimaryTextColor: yup
        .string()
        .nullable()
        .validLengthIfPresent(7, 'Color must be six digits')
        .validHex('Must be a valid hexadecimal color value')
        .optional(),
    brandEmail: yup.string().email().optional(),
    hppCreditEnabled: yup.boolean().optional(),
    hppCreditUnvalidatedEnabled: yup.boolean().optional(),
    hppGooglePayEnabled: yup.boolean().optional(),
    hppAchEnabled: yup.boolean().optional(),
    hppEftEnabled: yup.boolean().optional(),
    hppApplePayEnabled: yup.boolean().optional(),
    premiumFinanceEnabled: yup.boolean().optional(),
    hppMultiInvoiceEnabled: yup.boolean().optional(),
    hppSingleInvoiceValidationMode: yup.string().optional(),
    hppSingleAmountEnabled: yup.boolean().optional(),
    hppPayByInvoiceEnabled: yup.boolean().optional(),
    hppManageMyAccountEnabled: yup.boolean().optional(),
    hppLinkExpirationDays: yup.number().integer().positive().optional().oneOf(hppLinkExpirationDayChoices),
    hppLinkReminderDays: yup.number().integer().positive().optional(),
    hppLinkWarningDays: yup.number().integer().positive().optional(),
    hppCreditFeePercent: yup.number().max(maxCreditFeePercent).optional().min(0),
    hppAchFeeAmount: yup.number().max(maxAchFeeAmount).optional().min(0),
    hppEftFeeAmount: yup.number().max(maxEftFeeAmount).optional().min(0),
    hppConfig: hppConfigSchema.default(undefined),
    hppSubdomain: yup
        .string()
        .optional()
        .test({
        test: (subdomain) => !(subdomain === null || subdomain === void 0 ? void 0 : subdomain.trim()) || !subdomain.match(/[^a-zA-Z\d-]/),
        message: 'hppSubdomain may only contain letters, numbers, and -',
    })
        .test({
        test: (subdomain) => !(subdomain === null || subdomain === void 0 ? void 0 : subdomain.trim()) || !invalidHppSubdomains.includes(subdomain),
        message: 'hppSubdomain reserved',
    }),
    hppRedirectMerchantId: yup.number().nullable().optional(),
    paymentConfirmationEmails: yup.array().of(yup.string().email().required()).optional(),
    payoutSettlementEmails: yup.array().of(yup.string().email().required()).optional(),
    refundRequestedEmails: yup.array().of(yup.string().email().required()).optional(),
    refundProcessedEmails: yup.array().of(yup.string().email().required()).optional(),
    chargebackEmails: yup.array().of(yup.string().email().required()).optional(),
    voidProcessedEmails: yup.array().of(yup.string().email().required()).optional(),
    ccPaymentNotificationsEnabled: yup.boolean().optional(),
    ccPaymentNotificationEmails: yup.array().of(yup.string().email().required()).optional(),
    ccPaymentNotificationThresholdAmount: yup.number().nullable().optional(),
    isCcFeeCheckboxEnabled: yup.boolean().optional(),
    ccFeeThresholdAmount: yup.number().nullable().optional(),
    notificationEmails: yup.array().of(yup.string().email().required()).optional(),
    autoDebitToEpic: yup.boolean().optional(),
    lookupInvoicesBySiteId: yup.boolean().optional(),
    lookupAccountsByCustomId: yup.boolean().optional(),
    sftpEnabled: yup.boolean().optional(),
    sftpAddress: yup.string().optional(),
    sftpUsername: yup.string().optional(),
    sftpPassword: yup.string().optional(),
    sftpPrivateKey: yup.string().optional(),
    paymentConfirmationEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    payoutSettlementEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    refundRequestedEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    refundProcessedEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    chargebackEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    voidProcessedEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    ccPaymentNotificationEmailsServicingRoles: yup.array().of(yup.string().required()).optional(),
    epicOrganizations: yup.array().of(merchantEpicStructure.defined()).optional(),
    allEpicStructuresAssigned: yup.boolean().nullable().optional(),
    autopayEnabled: yup.boolean().optional(),
    autopayEnableAccount: yup.boolean().optional(),
    autopayEnablePolicy: yup.boolean().optional(),
    autopayEnableInvoice: yup.boolean().optional(),
    autopayShowAllInvoicesPolicies: yup.boolean().optional(),
    autopayEnrollmentChangeEmails: yup.array().of(yup.string().email().required()).optional(),
    autopayEnrollmentChangeServicingRoles: yup.array().of(yup.string().required()).optional(),
    autopayPaymentFailureEmails: yup.array().of(yup.string().email().required()).optional(),
    autopayPaymentFailureServicingRoles: yup.array().of(yup.string().required()).optional(),
    epicEnvironment: yup.string().optional(),
});
export const updateMerchantAccountAppliedSplitSettingsSchema = yup.object({
    accountHolderCode: yup.string().required(),
    appliedSplitBasisPoints: yup.number().integer().nullable(),
    appliedAchFlatFeeAmount: yup.number().integer().nullable(),
    appliedEftFlatFeeAmount: yup.number().integer().nullable(),
});
export const updateMerchantAccountEpicDataContextSchema = yup.object({
    accountHolderCode: yup.string().required(),
    epicDataContext: yup.string().required(),
});
export const updateMerchantAccountSettlementModelSchema = yup.object({
    merchantAccountId: yup.number().required(),
    settlementModel: yup.mixed().oneOf(Object.values(SettlementModel)).required(),
});
//# sourceMappingURL=merchantAccount.js.map